import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import { PubliekeLinkPaneel } from './PubliekeLinkModal';
import { MeldingDocumentPaneel } from './MeldingDocumentModal';
import { getMeldingDocumentGegevens } from '../api/api';
import { fmtDatumNL, leesbareFout } from '../util/format';

/* Alles wat naar de melder gaat, op één plek.
 *
 * Stond tot 06-09-2026 als twee losse regels in het Acties-menu. Dat menu was
 * negen regels lang geworden en het onderscheid tussen "iets versturen" en
 * "iets aan de registratie doen" was nergens te zien. Nu is het één regel, en
 * de keuze valt binnen het venster.
 *
 * Waarom een keuzescherm en geen tabbladen: er komen er waarschijnlijk drie
 * bij (bijwerking bevestigen, productieopgave, veldwerkgegevens). Vijf
 * tabbladen passen niet op een rij, en dan zou de vorm opnieuw om moeten.
 * Een lijst schaalt van twee naar vijf zonder dat er iets verandert.
 *
 * De gegevens van het Melding-document worden hier één keer opgehaald, niet in
 * het paneel: de ondertitel van het venster komt eruit, en het paneel hoeft ze
 * dan niet nog eens op te vragen. Het bouwen van de PDF begint pas als je het
 * paneel opent -- dat kost tien seconden en is zonde als je alleen de link
 * kwam halen.
 */

function ondertitel(m) {
  if (!m || !m.kenmerk) return null;
  const nummer = m.kenmerkType === 'verzoeknummer'
    ? `Verzoeknummer Omgevingsloket ${m.kenmerk}`
    : `Zaaknummer ${m.kenmerk}`;
  if (!m.ingangsdatum) return nummer;
  const toekomst = String(m.ingangsdatum).slice(0, 10) > new Date().toISOString().slice(0, 10);
  return `${nummer} · ${toekomst ? 'gaat in op' : 'geldt sinds'} ${fmtDatumNL(m.ingangsdatum)}`;
}

function Keuze({ titel, sub, onClick }) {
  return (
    <button type="button" className="corr-keuze" onClick={onClick}>
      <span className="corr-keuze-tekst">
        <span className="corr-keuze-titel">{titel}</span>
        <span className="corr-keuze-sub">{sub}</span>
      </span>
      <span className="corr-keuze-pijl" aria-hidden="true">›</span>
    </button>
  );
}

const TITELS = {
  publiek: 'Publieke pagina',
  document: 'Melding (document)',
};

export default function CorrespondentieModal({ isOpen, onClose, inrichtingId, toonDocument }) {
  const [keuze, setKeuze] = useState(null);
  const [doc, setDoc] = useState(null);
  const [fout, setFout] = useState(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    let levend = true;
    setKeuze(null); setDoc(null); setFout(null);
    if (!toonDocument) return undefined;
    (async () => {
      try {
        const res = await getMeldingDocumentGegevens(inrichtingId);
        if (levend) setDoc(res.data);
      } catch (e) {
        if (levend) setFout(leesbareFout(e));
      }
    })();
    return () => { levend = false; };
  }, [isOpen, inrichtingId, toonDocument]);

  const kop = ondertitel(doc && doc.melding);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={keuze ? TITELS[keuze] : 'Correspondentie'}
      maxWidth={640}
    >
      {keuze && (
        <button type="button" className="corr-terug" onClick={() => setKeuze(null)}>
          ‹ Correspondentie
        </button>
      )}
      {kop && <p className="meldoc-kop">{kop}</p>}
      {fout && <div className="alert alert-error" style={{ marginTop: 0 }}>{fout}</div>}

      {!keuze && (
        <>
          <Keuze
            titel="Publieke pagina"
            sub="Link en mailtekst"
            onClick={() => setKeuze('publiek')}
          />
          {toonDocument && (
            <Keuze
              titel="Melding (document)"
              sub={doc && !doc.kan ? 'Niet mogelijk' : 'PDF en mailtekst'}
              onClick={() => setKeuze('document')}
            />
          )}
          <div className="meldoc-voet">
            <button className="btn btn-ghost" onClick={onClose}>Sluiten</button>
          </div>
        </>
      )}

      {keuze === 'publiek' && (
        <PubliekeLinkPaneel inrichtingId={inrichtingId} onClose={onClose} />
      )}

      {keuze === 'document' && (
        doc
          ? <MeldingDocumentPaneel inrichtingId={inrichtingId} doc={doc} onClose={onClose} />
          : <p className="meldoc-meta" style={{ marginTop: 0 }}>Bezig met ophalen…</p>
      )}
    </Modal>
  );
}
